/**
 * What is under a point of the diagram: a box, an arrow, a bundle of arrows, a package or nothing;
 * in the blueprint also a pin or a box's "+", and arrows are its wires. Pure, in world units (the
 * caller turns its pixel tolerance into world units with the zoom).
 */
import { segmentDistance } from "../scene";
import { NO_FOCUS, countedBundles, shownArrows, type DiagramBundle, type DiagramFocus } from "./bundles";
import { type DiagramBox, type DiagramEdge, type DiagramLayout, type DiagramPackage } from "./layout";
import { pinHitAt, wireDistance, wiresOf, type PinHit } from "./pins";
import { PACKAGE_TAB } from "./constants";

/** Something of the diagram under the pointer. */
export type DiagramHit =
  | PinHit
  | { type: "box"; box: DiagramBox }
  | { type: "edge"; edge: DiagramEdge }
  | { type: "bundle"; bundle: DiagramBundle }
  | { type: "package"; pack: DiagramPackage }
  | { type: "background" };

/** Whether `(x, y)` is inside a rectangle. */
const inside = (r: { x: number; y: number; w: number; h: number }, x: number, y: number) => x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h;

/**
 * What is at `(x, y)`: a closed bundle's count, else the top box (the last drawn), else a folded
 * package (closed: it stands where its boxes would, so it is hit like one), else an arrow
 * or a closed bundle within `tolerance` (only those drawn while looking at `focus`), else the
 * innermost package (its tab or its frame's inside), else the background.
 */
export function hitAt(layout: DiagramLayout, x: number, y: number, tolerance: number, focus: DiagramFocus = NO_FOCUS): DiagramHit {
  const shown = shownArrows(layout, focus);
  // The tolerance is 6 pixels: the zoom follows.
  const counted = countedBundles(shown.bundles, 6 / tolerance);
  for (const bundle of shown.bundles) if ((!counted || counted.has(bundle)) && Math.hypot(x - (bundle.x1 + bundle.x2) / 2, y - (bundle.y1 + bundle.y2) / 2) <= countRadius(bundle, tolerance)) return { type: "bundle", bundle };
  for (let i = layout.boxes.length - 1; i >= 0; i--) {
    const box = layout.boxes[i]!;
    if (inside(box, x, y)) return { type: "box", box };
  }
  const closed = foldedAt(layout, x, y);
  if (closed) return closed;
  let best: DiagramHit | null = null;
  let bestDistance = tolerance;
  for (const edge of shown.edges) {
    const d = segmentDistance(x, y, edge.x1, edge.y1, edge.x2, edge.y2);
    if (d <= bestDistance) {
      best = { type: "edge", edge };
      bestDistance = d;
    }
  }
  for (const bundle of shown.bundles) {
    // A bundle is drawn thicker: a little more room around it.
    const d = segmentDistance(x, y, bundle.x1, bundle.y1, bundle.x2, bundle.y2) - tolerance / 2;
    if (d <= bestDistance) {
      best = { type: "bundle", bundle };
      bestDistance = d;
    }
  }
  if (best) return best;
  for (let i = layout.packages.length - 1; i >= 0; i--) {
    const pack = layout.packages[i]!;
    if (inside(pack, x, y) && (y >= pack.y + PACKAGE_TAB || x <= pack.x + tabWidth(pack))) return { type: "package", pack };
  }
  return { type: "background" };
}

/** The folded package under `(x, y)`, if any (the last drawn first). */
function foldedAt(layout: DiagramLayout, x: number, y: number): DiagramHit | null {
  for (let i = layout.packages.length - 1; i >= 0; i--) {
    const pack = layout.packages[i]!;
    if (pack.folded !== undefined && inside(pack, x, y)) return { type: "package", pack };
  }
  return null;
}

/** The width of a package's name tab (a guess from the name's length, wide enough to click). */
export function tabWidth(pack: Pick<DiagramPackage, "name" | "w">): number {
  return Math.min(pack.w, Math.max(60, pack.name.length * 7.5 + 20));
}

/**
 * The radius of a bundle's count badge in world units (its label is about 18 pixels round,
 * `tolerance` being 6 pixels in world units).
 */
export function countRadius(bundle: Pick<DiagramBundle, "links">, tolerance: number): number {
  return (tolerance / 6) * (8 + String(bundle.links.length).length * 3);
}

/**
 * What is at `(x, y)` in the blueprint: a pin (or, when `adding`, a box's "+"), else the top box,
 * else the nearest wire within `tolerance`, else the innermost package, else the background.
 */
export function blueprintHitAt(layout: DiagramLayout, x: number, y: number, tolerance: number, adding: boolean): DiagramHit {
  const pin = pinHitAt(layout, x, y, tolerance, adding);
  if (pin) return pin;
  for (let i = layout.boxes.length - 1; i >= 0; i--) {
    const box = layout.boxes[i]!;
    if (inside(box, x, y)) return { type: "box", box };
  }
  const closed = foldedAt(layout, x, y);
  if (closed) return closed;
  let best: DiagramHit | null = null;
  let bestDistance = tolerance;
  for (const wire of wiresOf(layout)) {
    const d = wireDistance(x, y, wire);
    if (d <= bestDistance) {
      best = { type: "edge", edge: wire.edge };
      bestDistance = d;
    }
  }
  if (best) return best;
  for (let i = layout.packages.length - 1; i >= 0; i--) {
    const pack = layout.packages[i]!;
    if (inside(pack, x, y) && (y >= pack.y + PACKAGE_TAB || x <= pack.x + tabWidth(pack))) return { type: "package", pack };
  }
  return { type: "background" };
}

/** The box a hit is on (a box, one of its pins or its "+"), if any. */
export function boxOfHit(hit: DiagramHit): DiagramBox | null {
  return hit.type === "box" || hit.type === "pin" || hit.type === "plus" ? hit.box : null;
}
